import { Button, Hairline, Loading, Message, Title } from "@components";

import styles from "styles/pages/reports/reports.module.scss";
import ContemplationXUses from "./ContemplationXUses";
import UsageBehavior from "./UsageBehavior";
import UsesAndExpiration from "./UsesAndExpiration";
import CampaignsInPeriod from "./CampaignsInPeriod";
import { Currency } from "shared/utils/Currency";
import { If } from "shared/components/control-statements";
import ReportFilters from "../ReportFilters";
import Filters from "../Filters";
import { useEffect, useRef, useState } from "react";
import { ReportsAPI } from "providers/ReportsAPI";
import { useParams } from "react-router";
import DistribuitionUses from "../Marketing/Coupons/DistribuitionUses";
import { Numbers } from "shared/utils/Numbers";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import DistribuitionUses2 from "../Marketing/Coupons/DistribuitionUses2";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";

const numbers = (data, isBeforeSeptember) => {
  return [
    {
      title: "Custo total",
      value: isBeforeSeptember ? "---" : Currency.formatCurrency(data.cost),
    },
    {
      title: "ROI total",
      value: isBeforeSeptember ? "---" : Numbers.formatFloat(data.roi),
    },
    {
      title: "Faturamento influenciado",
      value: Currency.formatCurrency(data.influenced),
      tooltip:
        "Soma do valor de todos os pedidos realizados por clientes atingidos por esta campanha em até 5 dias.",
    },
    {
      title: "Custo com disparos",
      value: Currency.formatCurrency(data.send_cost),
      tooltip: "Valor total gasto com mensageria.",
    },
    {
      title: "Faturamento com cupons",
      value: Currency.formatCurrency(data.total_with_discounts),
      tooltip:
        "Soma do valor de todos os pedidos que utilizaram cupons vinculados à esta campanha.",
    },
    {
      title: "Desconto de cupom",
      value: isBeforeSeptember
        ? "---"
        : Currency.formatCurrency(data.discounts),
      tooltip:
        "Soma do valor total de descontos aplicados nos pedidos que utilizaram cupons vinculados à esta campanha.",
    },
    {
      title: "ROI conservador",
      value: isBeforeSeptember
        ? "---"
        : Numbers.formatFloat(data.roi_without_discounts),
      tooltip:
        "Retorno sobre o investimento dos criativos. FÓRMULA: {{ ((Faturamento gerado - Descontos gerais) / Descontos totais) * 100 }}.",
    },
  ];
};

const quantities = (data) => {
  return [
    {
      title: "Quantidade de disparos",
      value: Numbers.formatInt(data.sends),
    },
    {
      title: "Quantidade de aberturas",
      value: Numbers.formatInt(data.opens),
    },
    {
      title: "Quantidade de cliques",
      value: Numbers.formatInt(data.clicks),
    },
    {
      title: "Quantidade de usos",
      value: Numbers.formatInt(data.uses),
    },
  ];
};

const indicators = (data) => {
  return [
    {
      title: "Taxa de conversão dos cupons",
      value: `${Numbers.percent(data.conversion_rate)}`,
      tooltip: "Porcentagem de cupons adicionados que foram utilizados.",
    },
    {
      title: "Taxa de expiração dos cupons",
      value: `${Numbers.percent(data.expiration_rate)}`,
      tooltip: "Porcentagem de cupons adicionados que expiraram.",
    },
    {
      title: "Ticket médio dos cupons",
      value: `${Currency.formatCurrency(data.average_ticket)}`,
      tooltip:
        "Soma do valor total dos pedidos que utilizaram cupons vinculados à esta campanha, divididos pela quantidade de pedidos que utilizaram cupons vinculados à esta campanha.",
    },
    {
      title: "Tempo médio para uso dos cupons",
      value: `${Numbers.formatFloat(data.time_to_use)} dias`,
      tooltip: "Média do tempo entre as adições e os usos dos cupons.",
    },
  ];
};

const BigNumber = ({ data }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <p className={styles.number}>{data.value}</p>
        </div>
        <div className={styles.title}>
          <p>{data.title}</p>
          <If condition={data.tooltip} title={data.tooltip}>
            <span className={styles.info}>
              <i className="fal fa-info-circle" />
            </span>
          </If>
        </div>
      </div>
    </div>
  );
};

const BigNumberFilled = ({ data }) => {
  return (
    <div className={styles.bigNumberFilled}>
      <div className="d-flex flex-column z-index-1">
        <div className="d-flex align-center">
          <p className={styles.number}>
            {Currency.formatCurrency(+data.value)}
          </p>
        </div>
        <div className={styles.title}>
          <p>{data.title}</p>
          <If condition={data.tooltip}>
            <span className={styles.info}>
              <i className="fal fa-info-circle" />
            </span>
          </If>
        </div>
      </div>
    </div>
  );
};

const CampaignIndicator = ({ data }) => {
  return (
    <div className={styles.campaignIndicator}>
      <div className={styles.value}>{data.value}</div>
      <p className={styles.title}>{data.title}</p>
    </div>
  );
};

export default function CampaignsDetails({ filters }) {
  const [openFilters, setOpenFilters] = useState(false);
  const [dateFilters, setDateFilters] = useState({});
  const [loading, setLoading] = useState(true);

  const filtersRef = useRef(null);

  const [data, setData] = useState({
    behavior: [],
    distribution: {},
    expirationUses: [],
    gainUses: {},
    period: [],
    keynumbers: {},
  });

  const [searchParams] = useSearchParams();
  const ids = searchParams.get("ids")?.split(",") || [];

  const fetchData = async () => {
    setLoading(true);
    const provider = new ReportsAPI();
    const response = await provider.flowCampaignsDetails({
      flow_id: ids.map((id) => id),
      ...filters,
    });
    setData(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    fetchUnits();
  }, [filters]);

  const september2025 = dayjs("2025-09-01T00:00:00.000Z");
  const currentBeforeSeptember = dayjs(data?.created_at || "").isBefore(
    september2025,
    "month"
  );

  const rangeBeforeSeptember =
    dayjs(filters?.start_date).isBefore(september2025) ||
    dayjs(filters?.end_date).isBefore(september2025);

  const isBeforeSeptember = rangeBeforeSeptember;

  const [label_dict, setLabelDict] = useState(new Map());

  const fetchUnits = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );
  };

  return (
    <>
      <Title
        title="Relatório de campanhas seleciondas"
        backTo={{
          url: "/relatorios/marketing/campanhas",
          name: "Relatórios de campanhas",
        }}
      >
        <Filters
          onRemoveFilter={(filter) => {
            filtersRef.current?.removeFilter(filter);
          }}
          filters={filters}
          units={label_dict}
        />
        <Button onClick={() => setOpenFilters(true)}>Filtrar</Button>
      </Title>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={isBeforeSeptember}>
        <Message type="warning" className="mb-15">
          Devido a data de criação dessa campanha, os seguintes dados não serão
          exibidos por falta de registro: Custo total, ROI total, Desconto de
          cupons, ROI com cupons.
        </Message>
      </If>

      <div className={styles.bigNumbersCampaign}>
        <>
          <BigNumberFilled
            data={{ title: "Faturamento total", value: data.keynumbers?.total }}
          />
          {numbers(data.keynumbers || {}, isBeforeSeptember).map((item) => (
            <BigNumber key={item.title} data={item} />
          ))}
        </>
      </div>

      <Hairline className="mb-15" />

      <div className={styles.bigNumbersCampaigns}>
        {quantities(data.keynumbers || {}).map((item) => (
          <CampaignIndicator key={item.title} data={item} />
        ))}
      </div>

      <div className={styles.bigNumbersCampaigns}>
        {indicators(data.keynumbers || {}).map((item) => (
          <BigNumber key={item.title} data={item} />
        ))}
      </div>

      <CampaignsInPeriod data={data.period} />
      <ContemplationXUses data={data.gainUses} />
      <UsageBehavior data={data.behavior} />
      <UsesAndExpiration data={data?.expirationUses} />
      <DistribuitionUses2
        data={data?.distribution}
        isBeforeSeptember={isBeforeSeptember}
      />

      {/* <ReportFilters
        ref={filtersRef}
        open={openFilters}
        onClose={() => setOpenFilters(false)}
        dateFiltersProp={dateFilters}
        onUpdateFilters={(filters) => {
          setFilters(filters);
        }}
        onUpdateDateFilters={(filters) => {
          setDateFilters(filters);
        }}
      /> */}
    </>
  );
}
